import React from 'react';
import {
  Cast,
  MonitorStop,
  Sliders,
  Sun,
} from 'lucide-react';
import type { RGB } from '../../types/ambilight';
import { rgbToHex } from '../../services/colorEngine';
import { useAmbilightContext } from '../../context/AmbilightContext';

export interface SlimBottomBarProps {
  isCapturing?: boolean;
  onToggleShare?: () => void;
  masterColor?: RGB;
  globalBorderWidth?: number;
  onChangeGlobalBorderWidth?: (width: number) => void;
  onOpenTune?: () => void;
}

export const SlimBottomBar: React.FC<SlimBottomBarProps> = (props) => {
  const context = useAmbilightContext();

  const isCapturing = props.isCapturing ?? context.isCapturing;
  const onToggleShare = props.onToggleShare ?? context.toggleShareScreen;
  const masterColor = props.masterColor ?? context.masterColor;
  const globalBorderWidth = props.globalBorderWidth ?? context.globalBorderWidth;
  const onChangeGlobalBorderWidth = props.onChangeGlobalBorderWidth ?? context.applyBorderToAll;
  const onOpenTune = props.onOpenTune ?? (() => context.setIsTuneOpen(true));
  const globalMaxBrightness = context.globalMaxBrightness;
  const updateGlobalMaxBrightness = context.updateGlobalMaxBrightness;

  const hex = rgbToHex(masterColor.r, masterColor.g, masterColor.b);

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 select-none">
      {/* Dock Ultra Slim Flotante en Cristal Oscuro */}
      <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-slate-950/85 backdrop-blur-xl border border-white/10 shadow-[0_12px_32px_rgba(0,0,0,0.7)] ring-1 ring-white/5">
        {/* 1. Botón Ultra Slim de Compartir Pantalla */}
        <button
          onClick={onToggleShare}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
            isCapturing
              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30'
              : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border border-purple-400/40 hover:from-purple-500 hover:to-indigo-500 shadow-md shadow-purple-600/30'
          }`}
          title={isCapturing ? 'Detener captura de pantalla' : 'Compartir pantalla para ambilight'}
        >
          {isCapturing ? (
            <>
              <MonitorStop className="w-3.5 h-3.5 text-rose-300 animate-pulse" />
              <span>Detener</span>
            </>
          ) : (
            <>
              <Cast className="w-3.5 h-3.5 text-cyan-300" />
              <span>Compartir Pantalla</span>
            </>
          )}
        </button>

        {/* Separador fino */}
        <div className="w-[1px] h-5 bg-white/10 mx-0.5" />

        {/* 2. Control Rápido de Ancho de Borde */}
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-white/80">
          <span className="text-white/50 text-[10px] font-mono">Borde:</span>
          <input
            type="range"
            min={15}
            max={250}
            step={5}
            value={globalBorderWidth}
            onChange={(e) => onChangeGlobalBorderWidth(Number(e.target.value))}
            className="w-20 h-1 cursor-pointer accent-purple-400"
            title={`Ancho de borde global: ${globalBorderWidth}px`}
          />
          <span className="font-mono text-cyan-300 text-[10px] w-8 text-right">
            {globalBorderWidth}px
          </span>
        </div>

        {/* Separador fino */}
        <div className="w-[1px] h-5 bg-white/10 mx-0.5" />

        {/* 3. Control Rápido de Brillo Máximo */}
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-white/80"
          title={`Brillo máximo global: ${globalMaxBrightness}% — Escala la curva gamma, el piso de dimming y la intensidad WebGL`}
        >
          <Sun className="w-3 h-3 text-amber-300 shrink-0" />
          <span className="text-white/50 text-[10px] font-mono">Máx:</span>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={globalMaxBrightness}
            onChange={(e) => updateGlobalMaxBrightness(Number(e.target.value))}
            className="w-20 h-1 cursor-pointer accent-amber-400"
            title={`Brillo máximo: ${globalMaxBrightness}%`}
          />
          <span className="font-mono text-amber-300 text-[10px] w-8 text-right">
            {globalMaxBrightness}%
          </span>
        </div>

        {/* Separador fino */}
        <div className="w-[1px] h-5 bg-white/10 mx-0.5" />

        {/* 4. Botón de Ajuste Fino */}
        <button
          onClick={onOpenTune}
          className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          title="Abrir ajuste fino de algoritmos"
        >
          <Sliders className="w-3.5 h-3.5" />
        </button>

        {/* 4. Muestra del Color Maestro Calculado */}
        <div
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/15 bg-black/40"
          title={`Color maestro actual: ${hex}`}
        >
          <span
            className="w-2.5 h-2.5 rounded-full shadow-sm"
            style={{
              backgroundColor: hex,
              boxShadow: `0 0 8px ${hex}`,
            }}
          />
          <span className="font-mono text-[10px] text-white/80">
            {hex}
          </span>
        </div>
      </div>
    </div>
  );
};
